/**
 * Main service for exporting lessons to Word format
 */

import { Document, Packer } from 'docx';
import { saveAs } from 'file-saver';
import { Lesson } from '../types';
import {
  createTitleSection,
  createObjectivesSection,
  createSuccessCriteriaSection,
  createStandardsSection,
  createMaterialsSection,
  createTimelineStepSections,
  createAssessmentSection,
  createMisconceptionsSection,
  createNotesSection,
  WordExportOptions
} from './wordTemplates';
import { downloadImageAsBuffer } from '../utils/imageDownloader';
import { createResourceVisualization } from '../utils/resourceVisualizer';
import { SECTION_PROPERTIES, EXTERNAL_STYLES_XML } from './wordStyles';

// Re-export WordExportOptions for external use
export type { WordExportOptions };

const DEFAULT_OPTIONS: WordExportOptions = {
  includeResources: true,
  includeAssessments: true,
  includeNotes: true,
  detailLevel: 'complete'
};

/**
 * Pre-downloads all images from the lesson in parallel
 * @param lesson The lesson to extract images from
 * @param options Export options
 * @returns A Map of image URL to image data with buffer and dimensions
 */
async function preloadImages(
  lesson: Lesson,
  options: WordExportOptions
): Promise<Map<string, { buffer: ArrayBuffer; width: number | null; height: number | null } | null>> {
  const imageCache = new Map<string, { buffer: ArrayBuffer; width: number | null; height: number | null } | null>();
  
  if (!options.includeResources || !lesson.timeline) {
    return imageCache;
  }
  
  // Collect all unique image URLs
  const imageUrls = new Set<string>();
  
  for (const step of lesson.timeline) {
    if (step.resources && step.resources.length > 0) {
      for (const stepResource of step.resources) {
        const resource = lesson.resources[stepResource.resourceId];
        if (resource && resource.type === 'image') {
          const visualization = createResourceVisualization(resource, stepResource.resourceId);
          if (visualization.imageUrl) {
            imageUrls.add(visualization.imageUrl);
          }
        }
      }
    }
  }
  
  // Download all images in parallel
  console.log(`Preloading ${imageUrls.size} images...`);
  const downloadPromises = Array.from(imageUrls).map(async (url) => {
    try {
      const buffer = await downloadImageAsBuffer(url);
      if (buffer) {
        // Get original image dimensions without resizing the buffer
        // The buffer will be kept as-is, only display dimensions will be adapted to A4
        const blob = new Blob([buffer]);
        const imageUrl = URL.createObjectURL(blob);
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageUrl;
        });
        URL.revokeObjectURL(imageUrl);
        
        // Return original buffer with original dimensions
        return { url, buffer, width: img.width, height: img.height };
      }
      return { url, buffer: null, width: null, height: null };
    } catch (error) {
      console.warn(`Failed to preload image: ${url}`, error);
      return { url, buffer: null, width: null, height: null };
    }
  });
  
  const results = await Promise.all(downloadPromises);
  
  // Populate cache with buffer and dimensions
  for (const result of results) {
    const { url, buffer, width, height } = result;
    if (buffer !== null && buffer !== undefined) {
      imageCache.set(url, { buffer, width: width ?? null, height: height ?? null });
    } else {
      imageCache.set(url, null);
    }
  }
  
  console.log(`Preloaded ${imageCache.size} images (${Array.from(imageCache.values()).filter(b => b !== null).length} successful)`);
  
  return imageCache;
}

/**
 * Exports a lesson to Word format
 * @param lesson The lesson to export
 * @param options Export options
 * @returns A Document instance ready to be saved
 */
export async function exportLessonToWord(
  lesson: Lesson,
  options: WordExportOptions = {}
): Promise<Document> {
  const exportOptions = { ...DEFAULT_OPTIONS, ...options };
  
  const children: any[] = [];
  
  try {
    // Title section
    children.push(...createTitleSection(lesson));
    
    // Objectives
    if (lesson.objectives && lesson.objectives.length > 0) {
      children.push(...createObjectivesSection(lesson));
    }
    
    // Success Criteria
    if (lesson.success_criteria && lesson.success_criteria.length > 0) {
      children.push(...createSuccessCriteriaSection(lesson));
    }
    
    // Standards and Prerequisites
    if ((lesson.standards_alignment && lesson.standards_alignment.length > 0) || lesson.prerequisites) {
      children.push(...createStandardsSection(lesson));
    }
    
    // Materials
    if ((lesson.materials.digital && lesson.materials.digital.length > 0) ||
        (lesson.materials.physical_alternatives && lesson.materials.physical_alternatives.length > 0)) {
      children.push(...createMaterialsSection(lesson));
    }
    
    // Preload all images in parallel before processing steps
    const imageCache = await preloadImages(lesson, exportOptions);
    
    // Timeline Steps
    if (lesson.timeline && lesson.timeline.length > 0) {
      for (let index = 0; index < lesson.timeline.length; index++) {
        const step = lesson.timeline[index];
        const stepSections = await createTimelineStepSections(step, index, lesson, exportOptions, imageCache);
        children.push(...stepSections);
      }
    }
    
    // Assessment Section
    if (exportOptions.includeAssessments) {
      children.push(...createAssessmentSection(lesson, exportOptions));
    }
    
    // Misconceptions and Differentiation
    children.push(...createMisconceptionsSection(lesson, exportOptions));
    
    // Notes Section
    if (exportOptions.includeNotes) {
      children.push(...createNotesSection(lesson, exportOptions));
    }
    
    // Filter out any null or undefined children to prevent metadata issues
    const validChildren = children.filter(child => child !== null && child !== undefined);
    
    // Ensure we have at least one valid child
    if (validChildren.length === 0) {
      const { Paragraph, TextRun } = await import('docx');
      validChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: 'Empty lesson'
            })
          ]
        })
      );
    }
    
    // Create document with Word 2023 compatibility
    const doc = new Document({
      creator: 'Education Application',
      title: lesson.metadata.title || 'Untitled Lesson',
      description: `Lesson plan: ${lesson.metadata.title || 'Untitled Lesson'}`,
      // External styles XML for Word 2023 compatibility (namespace w16du, latent styles, Aptos font)
      // This includes the Normal style marked as default and 376 latent styles
      externalStyles: EXTERNAL_STYLES_XML,
      sections: [
        {
          properties: SECTION_PROPERTIES,
          children: validChildren
        }
      ]
    });
    
    return doc;
  } catch (error) {
    console.error('Error creating Word document:', error);
    throw new Error(`Failed to export lesson to Word: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Exports a lesson to Word and triggers download
 * @param lesson The lesson to export
 * @param options Export options
 * @param filename Optional filename (defaults to lesson title)
 */
export async function exportAndDownloadLesson(
  lesson: Lesson,
  options: WordExportOptions = {},
  filename?: string
): Promise<void> {
  try {
    const doc = await exportLessonToWord(lesson, options);
    
    // Sanitize filename to prevent issues
    const sanitizeFilename = (name: string): string => {
      if (!name) return 'lesson';
      
      // Normalize Unicode characters (é -> e, etc.)
      let sanitized = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      
      // Replace special characters with underscores
      sanitized = sanitized.replace(/[^a-z0-9\s\-_]/gi, '_');
      
      // Replace spaces and multiple underscores
      sanitized = sanitized.replace(/\s+/g, '_');
      sanitized = sanitized.replace(/_+/g, '_');
      
      // Remove leading/trailing underscores
      sanitized = sanitized.replace(/^_+|_+$/g, '');
      
      // Limit length and ensure non-empty
      sanitized = sanitized.substring(0, 200);
      
      return sanitized || 'lesson';
    };
    
    const finalFilename = filename || `${sanitizeFilename(lesson.metadata.title || 'lesson')}.docx`;
    
    // Generate blob and download
    const blob = await Packer.toBlob(doc);
    saveAs(blob, finalFilename);
  } catch (error) {
    console.error('Error exporting lesson:', error);
    throw error;
  }
}
